/**
 * Device eligibility for "When → device" style condition pickers.
 * Mirrors scene wizard rules (DeviceStep + selectDevicesByRoom) and requires
 * non-empty _init_conditions_components so the property dropdown is never empty.
 */

import type { Device } from 'src/types/Device';
import {
	excludedCategoriesFromWhenConditions,
	excludedDeviceTypesFromWhenConditions,
} from 'src/components/Scenes/helpers/init_device_conditions';
import { isWallDisplay } from 'src/helpers/interface_helpers';
import { CCT_ONLY_DEVICES } from 'src/constants';
import { _init_conditions_components } from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';

/** Mirrors isCctOnlyDevice in interface_helpers without importing stores. */
const isCctOnlyDeviceType = (deviceType: string | undefined): boolean =>
	new RegExp(`^(${CCT_ONLY_DEVICES.join('|')})$`).test(deviceType || '');

const excludeShared = true;
const includeTypes = CCT_ONLY_DEVICES;

/**
 * Same device rules as DeviceStep after selectDevicesByRoom, plus the
 * non-room filters from selectDevicesByRoom (shared, category, type).
 */
export const isDeviceEligibleForWhenConditionDeviceStep = (
	device: Device | undefined | null,
): boolean => {
	if (!device?.id) {
		return false;
	}

	if (excludeShared && device.shared) {
		return false;
	}

	if (
		excludedCategoriesFromWhenConditions.length > 0 &&
		device.category &&
		excludedCategoriesFromWhenConditions.includes(device.category) &&
		!includeTypes.includes(device.type)
	) {
		return false;
	}

	if (
		excludedDeviceTypesFromWhenConditions.length > 0 &&
		excludedDeviceTypesFromWhenConditions.includes(device.type)
	) {
		return false;
	}

	if (device.category === 'module') {
		if (isCctOnlyDeviceType(device.type)) {
			return true;
		}
		return !/^(shell)$/.test(device.mode ?? '');
	}

	return !(isWallDisplay(device.type) && device.category !== 'relay');
};

export const deviceHasWhenConditionComponents = (
	device: Device,
	status: unknown,
	settings: unknown,
): boolean => {
	try {
		const components = _init_conditions_components(device, status, settings);
		return Array.isArray(components) && components.length > 0;
	} catch {
		return false;
	}
};

/**
 * Eligible per scene rules and must expose at least one condition property via
 * _init_conditions_components (hides devices with empty dropdowns).
 */
export const isSelectableForFlowDeviceCondition = (
	device: Device | undefined | null,
	status: unknown,
	settings: unknown,
): boolean => {
	if (!isDeviceEligibleForWhenConditionDeviceStep(device)) {
		return false;
	}

	return deviceHasWhenConditionComponents(device!, status, settings);
};
